<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="() => {}" :static="true" class="relative z-50">
      <!-- Backdrop -->
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      </TransitionChild>

      <!-- Dialog -->
      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-sm transform overflow-hidden rounded-2xl bg-[var(--input-background)] p-6 text-left align-middle shadow-xl transition-all">
              <div class="flex flex-col items-center text-center">
                <!-- Confirmation Icon -->
                <div class="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30">
                  <svg
                    class="h-10 w-10 text-blue-500 dark:text-blue-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M8.25 9V5.25A2.25 2.25 0 0 1 10.5 3h6a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 16.5 21h-6a2.25 2.25 0 0 1-2.25-2.25V15M12 9l3 3m0 0-3 3m3-3H2.25"
                    />
                  </svg>
                </div>

                <!-- Title -->
                <DialogTitle
                  as="h3"
                  class="text-lg font-semibold leading-6 mt-4 text-[var(--text-color)] dark:text-white"
                >
                  {{ title }}
                </DialogTitle>

                <!-- Description -->
                <div class="mt-2">
                  <p class="text-sm text-[var(--text-secondary)] dark:text-gray-400">
                    {{ message }}
                  </p>
                </div>

                <!-- Action Buttons -->
                <div class="mt-6 flex gap-3 w-full">
                  <button
                    type="button"
                    class="flex-1 inline-flex justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    @click="handlePrimaryAction"
                  >
                    {{ primaryButtonText }}
                  </button>
                  <button
                    type="button"
                    class="flex-1 inline-flex justify-center rounded-lg px-4 py-2 text-sm font-semibold text-[var(--text-color)] shadow-sm transition-colors bg-[var(--surface-border)] hover:bg-[var(--surface-hover)] focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    @click="handleSecondaryAction"
                  >
                    {{ secondaryButtonText }}
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { Dialog, DialogPanel, DialogTitle, TransitionRoot, TransitionChild } from '@headlessui/vue'

interface Props {
  isOpen: boolean
  title?: string
  message?: string
  primaryButtonText?: string
  secondaryButtonText?: string
  onPrimaryAction?: () => void
  onSecondaryAction?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  title: 'Confirm Action',
  message: 'Are you sure you want to proceed?',
  primaryButtonText: 'Confirm',
  secondaryButtonText: 'Cancel'
})

const emit = defineEmits<{
  primaryAction: []
  secondaryAction: []
  close: []
}>()

const handlePrimaryAction = () => {
  if (props.onPrimaryAction) {
    props.onPrimaryAction()
  }
  emit('primaryAction')
}

const handleSecondaryAction = () => {
  if (props.onSecondaryAction) {
    props.onSecondaryAction()
  }
  emit('secondaryAction')
}
</script>
